import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { getAuthUser, getAuthToken } from '../../utils/auth';
import { API_BASE_URL } from '../../config/api';
import FinanceWorldCanvas from './components/FinanceWorldCanvas';
import FinanceInputPanel from './components/FinanceInputPanel';
import CharacterCustomizerModal from './components/CharacterCustomizerModal';
import {
  FinancialAssets,
  FinancialLiabilities,
  FinancialCashFlow,
  CalculatedSummary,
  calculateFinances
} from './utils/financeCalculations';
import { CharacterProfile } from './types/myWealth';
import './MyWealth.css';

const DEFAULT_ASSETS: FinancialAssets = {
  cashAndSavings: 0,
  investments: 0,
  retirementAccounts: 0,
  propertyValue: 0,
  vehicles: 0,
  otherAssets: 0
};

const DEFAULT_LIABILITIES: FinancialLiabilities = {
  creditCardDebt: 0,
  studentLoans: 0,
  homeLoans: 0,
  otherDebts: 0
};

const DEFAULT_CASHFLOW: FinancialCashFlow = {
  monthlyIncome: 0,
  monthlyExpenses: 0
};

export const MyWealth: React.FC = () => {
  const navigate = useNavigate();
  const authUser = getAuthUser();
  const token = getAuthToken();

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState<string | null>(null);

  // Financial State
  const [currency, setCurrency] = useState('USD');
  const [assets, setAssets] = useState<FinancialAssets>(DEFAULT_ASSETS);
  const [liabilities, setLiabilities] = useState<FinancialLiabilities>(DEFAULT_LIABILITIES);
  const [cashFlow, setCashFlow] = useState<FinancialCashFlow>(DEFAULT_CASHFLOW);
  const [savedSnapshot, setSavedSnapshot] = useState<{
    assets: FinancialAssets;
    liabilities: FinancialLiabilities;
    cashFlow: FinancialCashFlow;
    currency: string;
  }>({
    assets: DEFAULT_ASSETS,
    liabilities: DEFAULT_LIABILITIES,
    cashFlow: DEFAULT_CASHFLOW,
    currency: 'USD'
  });

  // Character Profile State
  const [characterProfile, setCharacterProfile] = useState<CharacterProfile>({
    username: authUser?.name ? authUser.name.toLowerCase().replace(/[^a-z0-9_-]/g, '') : 'mywealth_user',
    displayName: authUser?.name || 'Explorer',
    bio: '',
    skinTone: '#f5cba7',
    hairStyle: 'short',
    hairColor: '#4a2e18',
    shirtColor: '#3498db',
    pantsColor: '#2c3e50',
    shoesColor: '#ffffff',
    accessory: 'none',
    publicHouseTheme: 'classic',
    publicDecorations: {
      rugColor: '#e74c3c',
      plantCount: 2,
      hasComputerDesk: true,
      hasBookshelf: true,
      hasAquarium: false,
      lightingMood: 'warm'
    },
    isPublic: true
  });

  const [isCustomizerOpen, setIsCustomizerOpen] = useState(false);

  // Recalculate summary in real-time as user types
  const calculated: CalculatedSummary = calculateFinances(assets, liabilities, cashFlow).calculated;

  // Unsaved changes check
  const hasUnsavedChanges =
    JSON.stringify(assets) !== JSON.stringify(savedSnapshot.assets) ||
    JSON.stringify(liabilities) !== JSON.stringify(savedSnapshot.liabilities) ||
    JSON.stringify(cashFlow) !== JSON.stringify(savedSnapshot.cashFlow) ||
    currency !== savedSnapshot.currency;

  // Fetch private finance & character profile
  const fetchData = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMsg(null);

    try {
      // 1. Fetch private finance
      const finRes = await fetch(`${API_BASE_URL}/finance-world/private`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (finRes.status === 401 || finRes.status === 403) {
        navigate('/signin', { state: { from: { pathname: '/finance-world' } } });
        return;
      }

      const finJson = await finRes.json();
      if (finJson.success && finJson.data) {
        const d = finJson.data;
        const loadedAssets = d.assets || DEFAULT_ASSETS;
        const loadedLiabilities = d.liabilities || DEFAULT_LIABILITIES;
        const loadedCashFlow = d.cashFlow || DEFAULT_CASHFLOW;
        const loadedCurrency = d.currency || 'USD';

        setAssets(loadedAssets);
        setLiabilities(loadedLiabilities);
        setCashFlow(loadedCashFlow);
        setCurrency(loadedCurrency);

        setSavedSnapshot({
          assets: loadedAssets,
          liabilities: loadedLiabilities,
          cashFlow: loadedCashFlow,
          currency: loadedCurrency
        });
      }

      // 2. Fetch character profile
      const charRes = await fetch(`${API_BASE_URL}/finance-world/character`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const charJson = await charRes.json();
      if (charJson.success && charJson.data) {
        setCharacterProfile((prev) => ({
          ...prev,
          ...charJson.data
        }));
      }
    } catch (err: any) {
      console.error('Failed to load private finance world data:', err);
      setErrorMsg('Could not load your financial world. Please check connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save financial snapshot
  const handleSaveFinances = async () => {
    if (!token) return;
    setSaving(true);
    setErrorMsg(null);
    setSaveSuccessMsg(null);

    try {
      const res = await fetch(`${API_BASE_URL}/finance-world/private`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          assets,
          liabilities,
          cashFlow,
          currency
        })
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || 'Failed to save financial profile');
      }

      setSavedSnapshot({
        assets,
        liabilities,
        cashFlow,
        currency
      });

      setSaveSuccessMsg('✨ Financial snapshot saved securely! Your 3D world is up to date.');
      setTimeout(() => setSaveSuccessMsg(null), 5000);
    } catch (err: any) {
      setErrorMsg(err.message || 'Error saving finances');
    } finally {
      setSaving(false);
    }
  };

  // Reset to last saved snapshot
  const handleResetSnapshot = () => {
    setAssets(savedSnapshot.assets);
    setLiabilities(savedSnapshot.liabilities);
    setCashFlow(savedSnapshot.cashFlow);
    setCurrency(savedSnapshot.currency);
  };

  // Save character cosmetic updates
  const handleSaveCharacter = async (updated: CharacterProfile) => {
    if (!token) return;
    const res = await fetch(`${API_BASE_URL}/finance-world/character`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(updated)
    });

    const json = await res.json();
    if (!res.ok || !json.success) {
      throw new Error(json.message || 'Failed to update character');
    }
    setCharacterProfile(json.data);
  };

  // Unauthorized state
  if (!token || !authUser) {
    return (
      <div className="mywealth-page-wrapper">
        <Helmet>
          <title>My Wealth — Private 3D Finance World</title>
          <meta
            name="description"
            content="Visualize your personal finances in a private, Habbo-inspired 3D blocky house."
          />
        </Helmet>
        <div className="mywealth-unauthorized-card">
          <div className="unauth-icon">🔒</div>
          <h2>Sign in to Access My Wealth</h2>
          <p>
            My Wealth is a completely private, authenticated 3D financial visualization world. Your
            numbers and net worth are securely protected and only visible to you.
          </p>
          <button
            type="button"
            className="action-btn save-btn"
            onClick={() =>
              navigate('/signin', { state: { from: { pathname: '/finance-world' } } })
            }
          >
            Sign In / Register
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mywealth-page-wrapper">
      <Helmet>
        <title>My Wealth — Personal 3D Finance World</title>
        <meta
          name="description"
          content="Track personal finances and watch your private 3D blocky world transform with your net worth."
        />
      </Helmet>

      {/* Main Header */}
      <header className="mywealth-header">
        <div className="mywealth-header-title-block">
          <div className="title-row">
            <h1 className="mywealth-title">My Wealth</h1>
            <span className="private-badge">🔒 100% Private</span>
          </div>
          <p className="mywealth-subtitle">
            Interactive Habbo-inspired 3D house reflecting your personal financial health in real time.
          </p>
        </div>

        <div className="mywealth-header-actions">
          {characterProfile.username && (
            <a
              href={`/profile/${characterProfile.username}`}
              target="_blank"
              rel="noreferrer"
              className="action-btn secondary-btn"
              title="Preview how your character appears to other visitors"
            >
              🌐 View Public Profile
            </a>
          )}
          <button
            type="button"
            className="action-btn customize-avatar-btn"
            onClick={() => setIsCustomizerOpen(true)}
          >
            🎨 Customize Character
          </button>
        </div>
      </header>

      {/* Alerts */}
      {saveSuccessMsg && <div className="alert-banner alert-success">{saveSuccessMsg}</div>}
      {errorMsg && <div className="alert-banner alert-error">{errorMsg}</div>}

      {/* Loading Skeleton */}
      {loading ? (
        <div className="mywealth-loading-container">
          <div className="canvas-spinner" />
          <p>Decrypting and loading your private 3D world...</p>
        </div>
      ) : (
        <main className="mywealth-content-layout">
          {/* 3D World Section */}
          <section className="mywealth-canvas-section" aria-label="3D Finance House Canvas">
            <FinanceWorldCanvas
              tier={calculated.visualizationTier}
              characterCosmetics={characterProfile}
              onOpenCustomizer={() => setIsCustomizerOpen(true)}
            />
          </section>

          {/* Financial Input and Summary Panel */}
          <section className="mywealth-panel-section" aria-label="Financial Input and Snapshot Dashboard">
            <FinanceInputPanel
              assets={assets}
              liabilities={liabilities}
              cashFlow={cashFlow}
              currency={currency}
              calculated={calculated}
              saving={saving}
              hasUnsavedChanges={hasUnsavedChanges}
              onAssetsChange={setAssets}
              onLiabilitiesChange={setLiabilities}
              onCashFlowChange={setCashFlow}
              onCurrencyChange={setCurrency}
              onSave={handleSaveFinances}
              onReset={handleResetSnapshot}
              onOpenCustomizer={() => setIsCustomizerOpen(true)}
              publicUsername={characterProfile.username}
            />
          </section>
        </main>
      )}

      {/* Character Customizer Modal */}
      <CharacterCustomizerModal
        initialProfile={characterProfile}
        isOpen={isCustomizerOpen}
        onClose={() => setIsCustomizerOpen(false)}
        onSave={handleSaveCharacter}
      />
    </div>
  );
};

export default MyWealth;
